// Browser-only helpers that turn a chosen background source (uploaded image,
// uploaded PDF, or an OpenStreetMap location) into ONE data URI that the
// location plan stores, renders and exports identically. pdf.js and Leaflet are
// bundled from npm (lazy-loaded chunks from our OWN origin) — no third-party
// CDN. OpenStreetMap tiles / Nominatim are contacted only after explicit user
// opt-in.

// ---- Image upload ---------------------------------------------------------
function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Downscale large images so they fit comfortably in localStorage. Small images
// (< ~1.2 MB) are kept verbatim to preserve crisp floor-plan lines.
export async function imageFileToDataUri(file, maxDim = 1800, quality = 0.85) {
  const dataUri = await readAsDataURL(file);
  if (file.size && file.size < 1.2 * 1024 * 1024) return dataUri;
  const img = await loadImage(dataUri);
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  if (scale >= 1) return dataUri;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

// ---- PDF upload (lazy pdf.js, bundled from npm) ---------------------------
let pdfjsPromise = null;
function ensurePdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  // Dynamic import -> separate webpack chunk served from our own origin.
  // Importing pdf.worker.entry sets window.pdfjsWorker (main-thread worker),
  // so pdf.js needs no external workerSrc URL.
  pdfjsPromise = import('pdfjs-dist/build/pdf')
    .then((mod) =>
      import('pdfjs-dist/build/pdf.worker.entry').then(() => {
        const lib = mod.default || mod;
        window.pdfjsLib = lib;
        return lib;
      })
    )
    .catch((e) => {
      pdfjsPromise = null;
      throw e;
    });
  return pdfjsPromise;
}

export async function pdfFileToDataUri(file, maxDim = 2000, quality = 0.9) {
  const pdfjsLib = await ensurePdfJs();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const pageNum = 1;
  const pdfPage = await pdf.getPage(pageNum);
  const base = pdfPage.getViewport({ scale: 1 });
  const scale = Math.min(maxDim / base.width, maxDim / base.height, 3);
  const viewport = pdfPage.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await pdfPage.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL('image/jpeg', quality);
}

// ---- OpenStreetMap snapshot ----------------------------------------------
// Lazy-load Leaflet (interactive preview map), bundled from npm (own origin) —
// no external CDN. OpenStreetMap tiles / Nominatim are only contacted after the
// user explicitly opts in (see LocationBackgroundControl).
let leafletPromise = null;
export function ensureLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  // Dynamic import -> separate webpack chunk (JS + CSS) from our own origin.
  leafletPromise = Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')])
    .then(([mod]) => {
      const L = mod.default || mod;
      window.L = L;
      return L;
    })
    .catch((e) => {
      leafletPromise = null;
      throw e;
    });
  return leafletPromise;
}

function lonLatToTile(lon, lat, z) {
  const n = Math.pow(2, z);
  const x = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

// Composite OSM tiles for a center/zoom into one image data URI. Tiles are
// loaded with CORS so the canvas isn't tainted and toDataURL works.
export async function osmToDataUri({ lat, lng, zoom }, width = 1400, height = 968) {
  const z = Math.max(1, Math.min(19, Math.round(zoom)));
  const n = Math.pow(2, z);
  const c = lonLatToTile(lng, lat, z);
  const centerPx = c.x * 256;
  const centerPy = c.y * 256;
  const originX = centerPx - width / 2;
  const originY = centerPy - height / 2;
  const x0 = Math.floor(originX / 256);
  const y0 = Math.floor(originY / 256);
  const x1 = Math.floor((originX + width) / 256);
  const y1 = Math.floor((originY + height) / 256);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e9e9e4';
  ctx.fillRect(0, 0, width, height);

  const jobs = [];
  for (let tx = x0; tx <= x1; tx++) {
    for (let ty = y0; ty <= y1; ty++) {
      const wrappedX = ((tx % n) + n) % n;
      if (ty < 0 || ty >= n) continue;
      const url = `https://tile.openstreetmap.org/${z}/${wrappedX}/${ty}.png`;
      const dx = tx * 256 - originX;
      const dy = ty * 256 - originY;
      jobs.push(
        loadImage(url, 'anonymous')
          .then((img) => ctx.drawImage(img, Math.round(dx), Math.round(dy), 256, 256))
          .catch(() => {})
      );
    }
  }
  await Promise.all(jobs);

  // OSM attribution (required by the tile usage policy).
  const label = '© OpenStreetMap-Mitwirkende';
  ctx.font = '12px sans-serif';
  const tw = ctx.measureText(label).width + 12;
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fillRect(width - tw, height - 20, tw, 20);
  ctx.fillStyle = '#333';
  ctx.fillText(label, width - tw + 6, height - 6);

  try {
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    throw new Error('osm-tainted');
  }
}

// Forward-geocode a place name to coordinates via the OSM Nominatim API.
export async function geocode(query) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('geocode-failed');
  const arr = await res.json();
  if (!arr || !arr.length) throw new Error('geocode-empty');
  return { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon), label: arr[0].display_name };
}
